import React from 'react';
import { useExam } from './ExamContext';
import RendererRegistry from './RendererRegistry';

/**
 * QuestionRenderer: Universal Component Factory
 * Dynamically resolves renderers based on group.questionType or group.type.
 * Renders phrase bank questions (Q31-Q36) as select dropdown menus matching Mock Exam 1 standards.
 */
const QuestionRenderer = ({ group }) => {
  const { answers, updateAnswer, setAnswer } = useExam();
  const handleAnswerChange = updateAnswer || setAnswer;

  // Defensive check: If group has no questions, return null
  if (!group || !group.questions || group.questions.length === 0) return null;

  const qType = group.questionType || group.type || '';
  const Component = RendererRegistry[qType];

  // If no registry component is found, use our robust inline universal renderer
  if (!Component) {
    const questionsList = group.questions || [];
    const phraseBank = group.phraseBank;

    return (
      <div id={`q-${group.groupId || 'group'}`} className="question-group-container" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {questionsList.map((q, idx) => {
          const qId = q.questionId || q.id || `Q${idx + 1}`;
          const qText = q.text || q.prompt || q.question || `Question ${idx + 1}`;
          const currentVal = answers[qId] || '';
          const subType = (q.type || qType).toUpperCase();

          const groupInstr = group.instructions || group.instruction || '';
          const isParagraphMatching = subType.includes('MATCHING') || subType.includes('PARAGRAPH') || 
            groupInstr.toUpperCase().includes('PARAGRAPH') ||
            (q.options && Object.keys(q.options).length > 0 && Object.keys(q.options).every(k => ['A','B','C','D','E','F','G','H'].includes(k.toUpperCase())));

          const isSummaryBankCompletion = subType.includes('SUMMARY') || phraseBank || 
            groupInstr.toUpperCase().includes('LIST OF PHRASES');

          const wordCount = currentVal.trim() === '' ? 0 : currentVal.trim().split(/\s+/).length;
          const maxWords = groupInstr.toLowerCase().includes('one word only') || q.maxWords === 1 ? 1 : 2;

          return (
            <div key={qId} className="question-item" style={{ marginBottom: '20px', padding: '16px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
              <p style={{ fontWeight: '600', color: '#1a202c', marginBottom: '12px', fontSize: '0.95rem', lineHeight: '1.4' }}>
                <strong>{qId}.</strong> {qText}
              </p>

              {subType.includes('TFNG') || subType.includes('YNNG') ? (
                <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
                  {(subType.includes('YNNG') ? ['YES', 'NO', 'NOT GIVEN'] : ['TRUE', 'FALSE', 'NOT GIVEN']).map((opt) => (
                    <label key={opt} style={{ cursor: 'pointer', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <input 
                        type="radio" 
                        name={qId} 
                        value={opt}
                        checked={currentVal === opt}
                        onChange={(e) => handleAnswerChange(qId, e.target.value)}
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              ) : isParagraphMatching ? (
                /* Clean, evenly distributed horizontal layout for paragraph letters A-F */
                <div style={{ display: 'flex', gap: '12px', marginTop: '12px', flexWrap: 'wrap' }}>
                  {(q.options ? Object.keys(q.options) : ['A', 'B', 'C', 'D', 'E', 'F']).map((letter) => (
                    <label 
                      key={letter} 
                      style={{ 
                        cursor: 'pointer', 
                        fontSize: '14px', 
                        fontWeight: '600', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px', 
                        background: currentVal === letter ? '#eff6ff' : '#f8fafc', 
                        padding: '8px 16px', 
                        border: currentVal === letter ? '1px solid #3b82f6' : '1px solid #cbd5e1', 
                        borderRadius: '6px',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <input 
                        type="radio" 
                        name={qId} 
                        value={letter}
                        checked={currentVal === letter}
                        onChange={(e) => handleAnswerChange(qId, e.target.value)}
                        style={{ accentColor: '#2563eb' }}
                      />
                      <span>{letter} {q.options ? `- ${q.options[letter]}` : ''}</span>
                    </label>
                  ))}
                </div>
              ) : isSummaryBankCompletion && phraseBank ? (
                /* Dropdown select menu matching Mock Exam 1 format for phrase bank summary completion (Q31-Q36) */
                <div style={{ marginTop: '10px' }}>
                  <select
                    value={currentVal}
                    onChange={(e) => handleAnswerChange(qId, e.target.value)}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', background: '#f8fafc', color: '#1e293b', cursor: 'pointer' }}
                  >
                    <option value="">-- Select option (A–{String.fromCharCode(64 + phraseBank.length)}) --</option>
                    {phraseBank.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.id}. {item.text}
                      </option>
                    ))}
                  </select>
                </div>
              ) : subType.includes('MCQ') || (q.options && typeof q.options === 'object') ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
                  {Object.entries(q.options || {}).map(([key, val]) => (
                    <label key={key} style={{ cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <input 
                        type="radio" 
                        name={qId} 
                        value={key}
                        checked={currentVal === key}
                        onChange={(e) => handleAnswerChange(qId, e.target.value)}
                        style={{ marginTop: '3px' }}
                      />
                      <span><strong>{key}.</strong> {val}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <div>
                  <input 
                    type="text" 
                    value={currentVal}
                    onChange={(e) => handleAnswerChange(qId, e.target.value)}
                    placeholder="Type your answer here..."
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '4px', border: '1px solid #cbd5e1', marginTop: '8px', fontSize: '14px' }}
                  />
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '5px' }}>
                    Word count: {wordCount} / {maxWords} max {maxWords === 1 ? 'word' : 'words'} (Strictly enforced)
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Maps individual questions within the group to the resolved component from registry
  return (
    <div id={`q-${group.groupId || 'group'}`} className="question-group-container">
      {(group.questions || []).map((q, idx) => {
        const qId = q.questionId || q.id || `Q${idx + 1}`;
        const qText = q.text || q.prompt || q.question || `Question ${idx + 1}`;
        const groupInstr = group.instructions || group.instruction || '';
        const currentVal = answers[qId] || '';

        return (
          <div key={qId} className="question-item" style={{ marginBottom: '20px', padding: '15px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
            <p style={{ fontWeight: '600', color: '#1a202c', marginBottom: '10px', fontSize: '0.95rem', lineHeight: '1.4' }}>
              <strong>{qId}.</strong> {qText}
            </p>
            <Component 
              question={{ ...q, questionId: qId, text: qText }}
              groupInstructions={groupInstr}
              value={currentVal}
              onChange={(val) => handleAnswerChange(qId, val)}
            />
          </div>
        );
      })}
    </div>
  );
};

export default QuestionRenderer;